import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useInfiniteQueryBuilder, useQueryBuilder } from "../../useBuilders";
import { BRAND_KIT, MEDIA, PALETTE } from "../../queryKeys";
import {
  BrandKitMediaPage,
  BrandKitMediaType,
  brandKitMediaApi,
} from "src/services/experiences/storyboardSettings/brandKitMedia";

const MY_UPLOADS = "MY_UPLOADS";

// Fetches workspace palettes and returns the id of the MEDIA palette (brandkit id).
export const useBrandKitPaletteIdQuery = () => {
  return useQueryBuilder(
    [BRAND_KIT, PALETTE],
    brandKitMediaApi.fetchPalettes,
    false,
    10 * 60 * 1000,
    false,
    (palettes) => palettes?.find((p: { type: string }) => p.type === "MEDIA")?.id ?? null,
  );
};

export const useBrandKitMediaQuery = (
  paletteId: string,
  mediaType: BrandKitMediaType,
  enabled = true,
) => {
  return useInfiniteQueryBuilder(
    [BRAND_KIT, MEDIA, paletteId, mediaType],
    ({ pageParam }) =>
      brandKitMediaApi.fetchBrandKitMedia({ paletteId, mediaType, page: pageParam }),
    0,
    (lastPage: BrandKitMediaPage) =>
      lastPage.currentPage < lastPage.totalPages - 1
        ? lastPage.currentPage + 1
        : undefined,
    undefined,
    enabled && Boolean(paletteId),
    false,
  );
};

export const useDeleteMediaMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) => brandKitMediaApi.deleteMedia(mediaId),
    onSuccess: (_data, mediaId) => {
      const predicate = (q: { queryKey: readonly unknown[] }) =>
        (q.queryKey as unknown[]).includes(MEDIA);

      queryClient.setQueriesData<InfiniteData<BrandKitMediaPage>>(
        { predicate },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              records: page.records.filter((r) => r.id !== mediaId),
            })),
          };
        },
      );
    },
  });
};

export const useMyUploadsQuery = (
  storyboard: string,
  mediaType: BrandKitMediaType,
  sort: string,
  order: string,
  query: string,
) => {
  return useInfiniteQueryBuilder(
    [MY_UPLOADS, MEDIA, storyboard, mediaType, sort, order, query],
    ({ pageParam }) =>
      brandKitMediaApi.fetchMyUploads({ storyboard, mediaType, page: pageParam, sort, order, query }),
    0,
    (lastPage: BrandKitMediaPage) =>
      lastPage.currentPage < lastPage.totalPages - 1
        ? lastPage.currentPage + 1
        : undefined,
    undefined,
    Boolean(storyboard),
    false,
  );
};
